import React, { useState, useMemo, useCallback } from 'react';
import useCalendarStore from '@/store/calendarStore';
import useInitCalendarData from '@/hooks/calendar/useCalendarData';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import isBetween from 'dayjs/plugin/isBetween';
import weekOfYear from 'dayjs/plugin/weekOfYear';
import WeeklyReportHeader from '@/components/weeklyreport/WeeklyReportHeader';
import Detail from '@/components/calendar/Detail';
import { useWeeklyMetrics } from '@/hooks/weeklyreport/useWeeklyMetrics';
import { useEventHandlers } from '@/hooks/weeklyreport/useEventHandlers';
import { useTrendCalculations } from '@/hooks/weeklyreport/useTrendCalculations';
import { useWeeklyDateCalculations } from '@/hooks/weeklyreport/useWeeklyDateCalculations';
import { useEventProcessing } from '@/hooks/weeklyreport/useEventProcessing';
import { useGlobalConjunctions } from '@/hooks/weeklyreport/useGlobalConjunctions';
import { useKoreanSatellites } from '@/hooks/weeklyreport/useKoreanSatellites';
import { useLaunchData } from '@/hooks/weeklyreport/useLaunchData';
import { useMonthlyData } from '@/hooks/weeklyreport/useMonthlyData';
import useDailyMetrics from '@/hooks/weeklyreport/useDailyMetrics';
import {
  CollisionRiskDashboard,
  SpaceEventsDashboard,
  KoreanSatellitesSection,
  NuriSatellitesSection,
  GlobalConjunctionsSection,
} from '@/components/weeklyreport';
import WeeklyInsightsSection from '@/components/weeklyreport/WeeklyInsightsSection';
import SpaceEventsSection from '@/components/weeklyreport/SpaceEventsSection';
import ScrollToTopButton from '@/components/ui/ScrollToTopButton';
import TableOfContentButton from '@/components/ui/TableOfContentButton';
import { useNuriSatellites } from '@/hooks/weeklyreport/useNuriSatellites';

dayjs.extend(utc);
dayjs.extend(isBetween);
dayjs.extend(weekOfYear);

const TOC_SECTIONS = [
  {
    id: 'collision-risk',
    label: 'Conjunction Assessment Summary',
    icon: '⚠️',
    bgGradient: 'bg-gradient-to-br from-red-100 to-red-200',
  },
  {
    id: 'korean-satellites',
    label: 'Korean Satellite Conjunctions',
    icon: '🛰️',
    bgGradient: 'bg-gradient-to-br from-red-100 to-red-200',
  },
  {
    id: 'nuri-satellites',
    label: 'Nuri Satellite Conjunctions',
    icon: '🛰️',
    bgGradient: 'bg-gradient-to-br from-blue-100 to-blue-200',
  },
  {
    id: 'global-conjunctions',
    label: 'Top Conjunctions',
    icon: '🌍',
    bgGradient: 'bg-gradient-to-br from-red-100 to-red-200',
  },
  {
    id: 'space-watch',
    label: 'Space Watch',
    icon: '🚀',
    bgGradient: 'bg-gradient-to-br from-blue-100 to-blue-200',
  },
  {
    id: 'space-events',
    label: 'Space Events',
    icon: '📅',
    bgGradient: 'bg-gradient-to-br from-blue-100 to-blue-200',
  },
  {
    id: 'weekly-insights',
    label: 'Weekly Insights',
    icon: '💡',
    bgGradient: 'bg-gradient-to-br from-blue-100 to-blue-200',
  },
];

const WeeklyReport: React.FC = () => {
  const [currentDate, setCurrentDate] = useState(dayjs());
  const [calendarDate, setCalendarDate] = useState(new Date());

  const handleCalendarDateChange = useCallback((dateInfo: any) => {
    const newDate = dateInfo.view.currentStart;
    setCalendarDate(newDate);
  }, []);

  useInitCalendarData(calendarDate);
  const {
    events,
    checkedEvent,
    isLoading: isCalendarLoading,
  } = useCalendarStore();

  const [calendarView, setCalendarView] = useState('dayGridMonth');
  const [koreanSatellitesSortBy, setKoreanSatellitesSortBy] = useState('DCA');
  const [nuriSatellitesSortBy, setNuriSatellitesSortBy] = useState('DCA');

  const { thisWeekRange, dayNavigationState, eventDateRanges } =
    useWeeklyDateCalculations(currentDate);
  const { thisWeek, lastWeek, twoWeeksAgo, threeWeeksAgo, isFetchingMetrics } =
    useWeeklyMetrics(currentDate);
  const { data: dailyMetrics, isLoading: isDailyMetricsLoading } =
    useDailyMetrics(currentDate);

  const { weeklyEvents, calendarEvents } = useEventProcessing(
    events,
    eventDateRanges.weekRange,
    calendarDate,
    checkedEvent
  );

  // Conjunction Monitoring Metrics
  const {
    urgentRiskTrendDisplay,
    highRiskTrendDisplay,
    totalMonitoredTrendDisplay,
  } = useTrendCalculations(dailyMetrics?.today, dailyMetrics?.yesterday);

  const {
    calendarRef,
    modalState,
    openModal,
    closeModal,
    handleRowClick,
    handleCalendarViewChange,
    handleEventsCardClick,
    handleCalendarEventClick,
  } = useEventHandlers();

  const trendChartData = useMemo(() => {
    const labels = ['3 Weeks Ago', '2 Weeks Ago', 'Last Week', 'This Week'];
    const data = [
      threeWeeksAgo.urgentRiskCount,
      twoWeeksAgo.urgentRiskCount,
      lastWeek.urgentRiskCount,
      thisWeek.urgentRiskCount,
    ];

    return {
      labels,
      datasets: [
        {
          label: 'Urgent Risk Events',
          data: data,
          borderColor: '#dc2626',
          backgroundColor: 'rgba(220, 38, 38, 0.1)',
          tension: 0.1,
        },
      ],
    };
  }, [thisWeek, lastWeek, twoWeeksAgo, threeWeeksAgo]);

  // Data fetching hooks
  const {
    filteredConjunctions,
    isGlobalLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useGlobalConjunctions(currentDate);

  const { topKoreanSatellites, isKoreanSatellitesLoading } =
    useKoreanSatellites(koreanSatellitesSortBy, currentDate);

  const { topNuriSatellites, isNuriSatellitesLoading } =
    useNuriSatellites(nuriSatellitesSortBy, currentDate);

  const { weeklyLaunchCount, nextThreeLaunches, isLaunchesLoading } =
    useLaunchData(thisWeekRange);

  const {
    newsData,
    monthlyGpCount,
    monthlyDebrisTrend,
    monthlyPayloadsTrend,
    monthlyKoreanSatellitesCount,
    isCurrentMonthGpLoading,
    isLastMonthGpLoading,
    isNewsLoading,
  } = useMonthlyData(currentDate);

  const dailySnapshot = dailyMetrics?.today;
  const displayedUrgentRiskCount = dailySnapshot?.urgentRiskCount ?? 0;
  const displayedHighRiskCount = dailySnapshot?.highRiskCount ?? 0;
  const displayedTotalMonitoredCount = dailySnapshot?.totalMonitoredCount ?? 0;
  const isMetricsLoading = isDailyMetricsLoading || !dailySnapshot;

  return (
    <div className='flex flex-col min-h-screen w-full mx-auto bg-gray-100 md:pb-[55px]'>
      <WeeklyReportHeader
        onPreviousDay={() =>
          setCurrentDate(prevDate => prevDate.subtract(1, 'day'))
        }
        onNextDay={() => setCurrentDate(prevDate => prevDate.add(1, 'day'))}
        isNextDayDisabled={dayNavigationState.isNextDayDisabled}
        isPreviousDayDisabled={dayNavigationState.isPreviousDayDisabled}
        currentDate={currentDate}
      />
      <div className='flex-grow w-full max-w-[1400px] mx-auto px-6 pb-16 box-border relative mt-[68px]'>
        <div className='flex flex-col w-full gap-6'>
          <div id='collision-risk' className='scroll-mt-24 w-full'>
            <CollisionRiskDashboard
              urgentRiskCount={displayedUrgentRiskCount}
              highRiskCount={displayedHighRiskCount}
              totalMonitoredCount={displayedTotalMonitoredCount}
              urgentRiskTrendDisplay={urgentRiskTrendDisplay}
              highRiskTrendDisplay={highRiskTrendDisplay}
              totalMonitoredTrendDisplay={totalMonitoredTrendDisplay}
              isFetchingMetrics={isMetricsLoading}
              dateRange={{
                start: dailyMetrics?.today
                  ? currentDate
                    .utc()
                    .startOf('day')
                    .subtract(1, 'day')
                    .format('YYYY-MM-DDTHH:mm:ss[Z]')
                  : '',
                end: dailyMetrics?.today
                  ? currentDate
                    .utc()
                    .startOf('day')
                    .subtract(1, 'day')
                    .add(3, 'day')
                    .format('YYYY-MM-DDTHH:mm:ss[Z]')
                  : '',
              }}
            />
          </div>

          <div id='korean-satellites' className='scroll-mt-24 w-full'>
            <KoreanSatellitesSection
              topKoreanSatellites={topKoreanSatellites}
              isKoreanSatellitesLoading={isKoreanSatellitesLoading}
              koreanSatellitesSortBy={koreanSatellitesSortBy}
              onSortChange={setKoreanSatellitesSortBy}
              onRowClick={handleRowClick}
            />
          </div>

          <div id='nuri-satellites' className='scroll-mt-24 w-full'>
            <NuriSatellitesSection
              topNuriSatellites={topNuriSatellites}
              isNuriSatellitesLoading={isNuriSatellitesLoading}
              nuriSatellitesSortBy={nuriSatellitesSortBy}
              onSortChange={setNuriSatellitesSortBy}
              onRowClick={handleRowClick}
            />
          </div>

          <div id='global-conjunctions' className='scroll-mt-24 w-full'>
            <GlobalConjunctionsSection
              filteredConjunctions={filteredConjunctions}
              isGlobalLoading={isGlobalLoading}
              hasNextPage={hasNextPage}
              isFetchingNextPage={isFetchingNextPage}
              onRowClick={handleRowClick}
              onLoadMore={fetchNextPage}
            />
          </div>

          <div id='space-watch' className='scroll-mt-24 w-full'>
            <SpaceEventsDashboard
              allEvents={weeklyEvents}
              weeklyLaunchCount={weeklyLaunchCount}
              nextThreeLaunches={nextThreeLaunches}
              isLaunchesLoading={isLaunchesLoading}
              onEventsCardClick={handleEventsCardClick}
            />
          </div>
          <div id='space-events' className='scroll-mt-24 w-full'>
            <SpaceEventsSection
              allEvents={weeklyEvents}
              calendarEvents={calendarEvents}
              calendarView={calendarView}
              calendarDate={calendarDate}
              isCalendarLoading={isCalendarLoading}
              calendarRef={calendarRef}
              onEventCardClick={openModal}
              onCalendarViewChange={handleCalendarViewChange}
              onCalendarDateChange={handleCalendarDateChange}
              onCalendarEventClick={handleCalendarEventClick}
              setCalendarView={setCalendarView}
            />
          </div>

          <div id='weekly-insights' className='scroll-mt-24 w-full'>
            <WeeklyInsightsSection
              trendChartData={trendChartData}
              isFetchingMetrics={isFetchingMetrics}
              monthlyGpCount={monthlyGpCount}
              monthlyDebrisTrend={monthlyDebrisTrend}
              monthlyPayloadsTrend={monthlyPayloadsTrend}
              monthlyKoreanSatellitesCount={monthlyKoreanSatellitesCount}
              isCurrentMonthGpLoading={isCurrentMonthGpLoading}
              isLastMonthGpLoading={isLastMonthGpLoading}
              newsData={newsData}
              isNewsLoading={isNewsLoading}
            />
          </div>

          <div className='bg-transparent text-black pb-8 py-4 px-5 flex items-center justify-start gap-3'>
            <button
              className='bg-transparent text-[#949494] cursor-pointer text-base font-medium'
              onClick={() => window.print()}
            >
              Download PDF
            </button>
          </div>
        </div>
      </div>

      {modalState.isOpen && (
        <div
          className='fixed inset-0 bg-black/50 flex justify-center items-center z-[1000] p-4'
          onClick={closeModal}
        >
          <div
            className='bg-white rounded-lg w-full max-w-[800px] max-h-[calc(100vh-10rem)] md:max-h-[85vh] overflow-hidden flex flex-col'
            onClick={e => e.stopPropagation()}
          >
            <div className='flex justify-end p-4 border-b border-slate-200 flex-shrink-0'>
              <button
                className='bg-transparent border-none text-2xl cursor-pointer text-slate-500 hover:text-slate-800'
                onClick={closeModal}
              >
                ×
              </button>
            </div>
            <div className='flex-1 overflow-y-auto p-0 min-h-0'>
              <Detail eventId={modalState.eventId} hideCloseButton={true} />
            </div>
          </div>
        </div>
      )}

      <ScrollToTopButton />
      <TableOfContentButton sections={TOC_SECTIONS} />
    </div>
  );
};

export default WeeklyReport;
